import { TechnicianAppointmentsItem } from '@myapp/contract'
import { resolveBreakForDay } from '../../../entities/time-entry/model/useBreakBlock'
import { isWorkingDay } from '../../../shared/lib/timeline/weekHelpers'
import { getHourFromIso } from './calendarConstants'
import { getTimeEntryCategoryLabel } from '../../../entities/time-entry'
import type { ConflictItem } from '../../calendar-planning-overlay/model/checkCollisions'

function overlaps(startA: number, endA: number, startB: number, endB: number): boolean {
  return startA < endB && startB < endA
}

function toMs(iso: string): number {
  return new Date(iso).getTime()
}

function formatTime(ms: number): string {
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

interface PauseRange {
  startMs: number
  endMs: number
  dayIso: string
}

interface CheckOptions {
  excludeTMId?: number
  excludeBreak?: boolean
}

export function checkPauseCollisions(
  range: PauseRange,
  technicianData: TechnicianAppointmentsItem | null,
  options?: CheckOptions,
): ConflictItem[] {
  const { startMs, endMs, dayIso } = range
  const conflicts: ConflictItem[] = []

  if (!technicianData) return conflicts

  if (!isWorkingDay(technicianData.workingDays, dayIso)) {
    conflicts.push({ label: 'Kein Arbeitstag', timeLabel: '' })
    return conflicts
  }

  const shiftStartHour = technicianData.shiftStart ? getHourFromIso(technicianData.shiftStart) : null
  const shiftEndHour = technicianData.shiftEnd ? getHourFromIso(technicianData.shiftEnd) : null
  const rangeStartHour = new Date(startMs).getHours() + new Date(startMs).getMinutes() / 60
  const rangeEndHour = new Date(endMs).getHours() + new Date(endMs).getMinutes() / 60

  if (shiftStartHour != null && shiftEndHour != null && (rangeStartHour < shiftStartHour || rangeEndHour > shiftEndHour)) {
    conflicts.push({ label: 'Außerhalb der Arbeitszeit', timeLabel: '' })
  }

  const dayAppointments = technicianData.appointments.filter(
    (a) => !a.needsRescheduling && new Date(a.plannedStart).toISOString().slice(0, 10) === dayIso,
  )

  for (const appt of dayAppointments) {
    const isCompleted = appt.status === 'COMPLETED' || appt.status === 'COMPLETED_WITH_FOLLOW_UP'
    const hasActualData = isCompleted && appt.actualStart != null && appt.actualTravelDuration != null && appt.actualWorkDuration != null

    const travelDur = hasActualData ? appt.actualTravelDuration! : appt.plannedTravelDuration
    const workDur = hasActualData ? appt.actualWorkDuration! : appt.plannedWorkDuration
    const departureDur = (hasActualData && appt.actualDepartureDuration != null)
      ? appt.actualDepartureDuration
      : appt.plannedDepartureDuration

    const workStartMs = hasActualData ? toMs(appt.actualStart!) : toMs(appt.plannedStart)
    const apptStart = workStartMs - travelDur * 60_000
    const apptEnd = workStartMs + (workDur + departureDur) * 60_000

    if (overlaps(startMs, endMs, apptStart, apptEnd)) {
      const address = appt.workOrder?.external.gebaeude ?? 'Termin'
      conflicts.push({ label: address, timeLabel: formatTime(workStartMs) })
    }
  }

  const dayStart = new Date(`${dayIso}T00:00:00`).getTime()
  const dayEnd = new Date(`${dayIso}T23:59:59.999`).getTime()

  const dayTMEntries = technicianData.timeManagementEntries.filter(
    (e) => e.id !== options?.excludeTMId && toMs(e.startTime) <= dayEnd && toMs(e.endTime) >= dayStart,
  )

  for (const entry of dayTMEntries) {
    const entryStart = Math.max(toMs(entry.startTime), dayStart)
    const entryEnd = Math.min(toMs(entry.endTime), dayEnd)
    if (overlaps(startMs, endMs, entryStart, entryEnd)) {
      conflicts.push({ label: getTimeEntryCategoryLabel(entry.category), timeLabel: formatTime(entryStart) })
    }
  }

  if (!options?.excludeBreak) {
    const breakBlock = resolveBreakForDay(technicianData, dayIso)
    const breakStart = toMs(breakBlock.startTimeIso)
    const breakEnd = breakStart + 30 * 60_000
    if (overlaps(startMs, endMs, breakStart, breakEnd)) {
      conflicts.push({ label: 'Pause', timeLabel: formatTime(breakStart) })
    }
  }

  return conflicts
}
